// The payments of one booking, as the desk sheets list them (Départs,
// Arrivées, Non facturés). One line per payment — when · method · amount —
// because HOW a guest paid is part of the checks (operator 2026-09-25). « When »
// is the time it was keyed when known (« 24/09 16:37 », §8.8j), else Vega's
// date. A payment that is not by card is set in bold, so it stands out on the
// printed sheet; a refund or an adjustment (neither card nor not) stays plain.
//
// §8.8o (operator 2026-09-26): the lines no longer wrap — each stays on one
// line, the three parts aligned down the list, and the list sits under the
// total it makes up rather than in the guest's cell. A payment made on the
// sheet's own day shows its time alone (the column says which day), an
// earlier one its date — with the time too on Départs, on hover on Arrivées.
// A single payment equal to its cell's total does not repeat the amount. Both
// sheets split a booking's payments into « before the day » and « on the day »
// (§8.8o, operator 2026-09-27 on MARWIN's check-in payment shown on Départs).
//
// A payment taken in the Encaissement form that Vega does not show yet carries
// a red ⚠ (§8.8k, operator 2026-09-26): the desk still keys every payment into
// Vega by hand, and the mark stays until the feed reads it there — same
// booking, same amount, same kind of tender. Once it does, Vega's own line
// replaces it, so the mark never sits on a payment Vega already has.

import type { IsoDate, PaymentLine } from '@lemontaigu/pms-core';
import { fmtAmount } from './logic.js';
import { dayMonth, paymentBucket, paymentDay, paymentStamp } from './departureSheet.js';

const SOURCE_TITLE: Record<PaymentLine['source'], string> = {
  arrhes: 'Arrhes Vega',
  reglement: 'Règlement Vega (facture)',
  encaissement: 'Pris au formulaire — pas encore saisi dans Vega (même résa, même montant, même mode)',
};

/** The red ⚠ of a payment Vega does not show yet. */
export function NotInVega() {
  return (
    <span className="pms-not-in-vega" role="img" aria-label="Pas encore dans Vega" title="Pas encore saisi dans Vega">
      ⚠
    </span>
  );
}

export interface PaymentListProps {
  payments: PaymentLine[];
  /** The sheet's day: a payment made that day shows its time only. */
  day?: IsoDate;
  /** An earlier payment's stamp: date and time (Départs, §8.8j) or the date
   *  alone, the time on hover (Arrivées' « Arrhes »). */
  older?: 'stamp' | 'date';
  /** The cell's total: a lone payment equal to it does not repeat its amount. */
  total?: number;
}

/** « 16:37 » for a payment of the sheet's day, else its date (and time). */
function when(p: PaymentLine, day: IsoDate | undefined, older: 'stamp' | 'date'): string {
  const full = paymentStamp(p);
  if (day && paymentDay(p) === day) return p.at ? full.slice(-5) : dayMonth(day);
  return older === 'date' ? dayMonth(p.date ?? paymentDay(p)) : full;
}

export function PaymentList({ payments, day, older = 'stamp', total }: PaymentListProps) {
  if (payments.length === 0) return null;
  const lone = payments.length === 1 && total !== undefined && payments[0]!.cents === total;
  return (
    <ul className="pms-pay-list">
      {payments.map((p, i) => (
        <li
          key={i}
          className={`pms-pay${p.isCard === false ? ' is-noncard' : ''}${p.source === 'encaissement' ? ' is-not-in-vega' : ''}`}
          // Only a payment OF the sheet's day is counted on its band, so only
          // one can light up (MARWIN's check-in payment of 24/09 lit on 25/09).
          data-bucket={day && paymentDay(p) === day ? (paymentBucket(p) ?? undefined) : undefined}
          title={`${paymentStamp(p)} · ${SOURCE_TITLE[p.source]}`}
        >
          {/* The « · » are in the text (a read-out, a copy) but not on screen,
              where the three parts are aligned columns. */}
          <span className="pms-pay-when">
            {p.source === 'encaissement' && <NotInVega />}
            {when(p, day, older)}
            <span className="pms-sep"> · </span>
          </span>{' '}
          <span className="pms-pay-how">
            {p.label ?? p.method ?? '?'}
            {!lone && <span className="pms-sep"> · </span>}
          </span>{' '}
          <span className="pms-pay-amt">{lone ? '' : fmtAmount(p.cents)}</span>
        </li>
      ))}
    </ul>
  );
}
